import { Alert, Button, Card, Form, Input, Space, Tag, Typography } from "antd";
import type { Customer, Page, Ship, ShipApply, ShipApplyVo, User } from "@zhihaoscm/entity";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { Pagination as ZhPagination, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { APPLY_STATE } from "@/meta/logistics/ship";
import { useUserStore } from "@/stores/react/authStore";

export interface ShipCertificationQuery {
    page: number;
    size: number;
    keyword?: string;
    customerId?: number;
    state?: number[];
    sortKey?: string;
    sortOrder?: string;
}

export interface ShipCertificationRow {
    shipApply: Partial<ShipApply>;
    shipVo?: { ship?: Partial<Ship> };
    customer?: Partial<Customer>;
    user?: Partial<User>;
}

export interface ShipCertificationPageProps {
    loadApplications?: (query: ShipCertificationQuery) => Promise<Page<ShipCertificationRow>>;
}

/** 复用 Vue 船舶认证审核分页接口，保留历史查询参数命名。 */
export async function loadShipCertification(query: ShipCertificationQuery): Promise<Page<ShipCertificationRow>> {
    const response = await shippingRequestHttp.service.get<Page<ShipCertificationRow>>("/ship/apply/paging", {
        params: query
    });
    return response.data;
}

function text(value: unknown): string {
    return value == null || value === "" ? DEFAULT_VALUE : String(value);
}

function dateText(value: unknown): string {
    return typeof value === "string" && value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE;
}

/** 物流管理-认证审核列表，覆盖 Vue 首屏字段、筛选和详情/审核入口。 */
export function ShipCertificationPage({ loadApplications = loadShipCertification }: ShipCertificationPageProps) {
    // 沿用 Vue userStore.banButton：保留审核入口，但在全局按钮禁用权限下禁止提交操作。
    const banButton = useUserStore(user => user.banButton);
    const [rows, setRows] = useState<ShipCertificationRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<ShipCertificationQuery>({ page: 1, size: 20 });
    const [keyword, setKeyword] = useState("");
    const [state, setState] = useState<number[]>([]);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        let disposed = false;
        void loadApplications(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage("");
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("认证审核列表加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadApplications, query]);

    const submitFilters = () => {
        const nextQuery: ShipCertificationQuery = { page: 1, size: 20 };
        if (keyword.trim()) nextQuery.keyword = keyword.trim();
        if (state.length) nextQuery.state = state;
        setQuery(nextQuery);
    };

    // 保留 Vue ProTable 的创建时间排序参数，并转换为旧接口字段。
    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
            if (prop !== "createdTime") return;
            setQuery(current => {
                const nextQuery = { ...current, page: 1 };
                if (!order) {
                    delete nextQuery.sortKey;
                    delete nextQuery.sortOrder;
                } else {
                    nextQuery.sortKey = "created_time";
                    nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                }
                return nextQuery;
            });
        },
        []
    );

    // 用 zhihao-ui-react 的列契约承接旧 Vue 船舶认证 ProTable 字段和审核插槽。
    const columns = useMemo<TableColumnProps<ShipCertificationRow>[]>(
        () => [
            {
                key: "mmsi",
                label: "MMSI",
                prop: "mmsi",
                render: ({ row }) => (
                    <Link to={`/logistics/shipManage/shipCertification/info/${row.shipApply.id}`}>
                        {text(row.shipApply.shipId)}
                    </Link>
                )
            },
            {
                key: "name",
                label: "AIS船名",
                prop: "name",
                render: ({ row }) => text(row.shipVo?.ship?.cnname || row.shipVo?.ship?.name)
            },
            {
                key: "customer",
                label: "认证单位",
                prop: "customerId",
                render: ({ row }) => {
                    const institution = row.customer?.institutionName;
                    const realName = row.customer?.realName;
                    return institution ? `${institution}-${text(realName)}` : text(realName);
                }
            },
            {
                key: "createdTime",
                label: "创建时间",
                prop: "createdTime",
                sortable: true,
                render: ({ row }) => dateText(row.shipApply.createdTime)
            },
            {
                key: "state",
                label: "状态",
                prop: "shipApply.state",
                render: ({ row }) => (
                    <Tag>{APPLY_STATE.getIntroByCode(Number(row.shipApply.state)) || DEFAULT_VALUE}</Tag>
                )
            },
            { key: "reviewer", label: "审核人", prop: "reviewer", render: ({ row }) => text(row.user?.name) },
            {
                key: "operation",
                label: "操作",
                prop: "operation",
                fixed: "right",
                render: ({ row }) => (
                    <Space>
                        <Link to={`/logistics/shipManage/shipCertification/info/${row.shipApply.id}`}>
                            <Button type="link">查看</Button>
                        </Link>
                        {Number(row.shipApply.state) === APPLY_STATE.PENDING_APPROVAL.code ? (
                            <Link to={`/logistics/shipManage/shipCertification/apply/${row.shipApply.id}`}>
                                <Button aria-label={`审核-${row.shipApply.id}`} disabled={banButton} type="link">
                                    审核
                                </Button>
                            </Link>
                        ) : null}
                    </Space>
                )
            }
        ],
        []
    );

    return (
        <section aria-label="ship-certification-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <Form layout="inline" onFinish={submitFilters}>
                    <Form.Item label="MMSI或船名">
                        <Input
                            aria-label="MMSI或船名"
                            placeholder="请输入船舶名关键词或MMSI搜索"
                            value={keyword}
                            onChange={event => setKeyword(event.target.value)}
                        />
                    </Form.Item>
                    <Form.Item label="认证状态">
                        <select
                            aria-label="认证状态"
                            multiple
                            value={state.map(String)}
                            onChange={event => {
                                const selected = Array.from(event.currentTarget.selectedOptions).map(option =>
                                    Number(option.value)
                                );
                                // 兼容 jsdom/旧调用方只注入 target.value 的单选变更事件。
                                setState(
                                    selected.length
                                        ? selected
                                        : event.currentTarget.value
                                          ? [Number(event.currentTarget.value)]
                                          : []
                                );
                            }}
                        >
                            {APPLY_STATE.options.map(option => (
                                <option key={String(option.value)} value={String(option.value)}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </Form.Item>
                    <Button aria-label="查询认证" htmlType="submit" type="primary">
                        查询
                    </Button>
                </Form>
            </Card>
            <Card>
                <ZhTable<ShipCertificationRow>
                    rowKey={row => String(row.shipApply.id)}
                    data={rows}
                    columns={columns}
                    emptyText="暂无认证审核记录"
                    pagination={false}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                认证审核列表
                            </Typography.Title>

                            {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                hideOnSinglePage
                                onChange={page => setQuery(current => ({ ...current, page }))}
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

// 保留 ShipApplyVo 名称导入关系，便于后续详情任务复用旧 VO 映射时不改接口层。
export type LegacyShipApplyVo = ShipApplyVo;

export default ShipCertificationPage;
